import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Image, Eye, PenSquare, Send, Loader2, X, Camera, Bold, Italic, Heading1, Heading2, Heading3, List, ListOrdered, Quote, Code, Minus, Link as LinkIcon, Undo, Redo, Type, AlertCircle } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useNostrQuery } from '../hooks/useNostrQuery';
import { uploadImage } from '../lib/imageUpload';
import { INK_NAME, INK_MARK, inkArticlePath, inkArticleUrl, inkBaseFor } from '../lib/ink';

// ── Rich Text Editor Component ──────────────────────────────────────
// A WYSIWYG editor using contentEditable that outputs markdown.
// No markdown knowledge needed — just type and format with the toolbar.

interface RichEditorProps {
  initialHtml: string;
  onChange: (html: string) => void;
  onImageUpload: () => void;
  uploadingInline: boolean;
  placeholder?: string;
}

function RichEditor({ initialHtml, onChange, onImageUpload, uploadingInline, placeholder }: RichEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
  const [activeBlock, setActiveBlock] = useState('p');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const savedSelection = useRef<Range | null>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const isInitialized = useRef(false);

  // Initialize content
  useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      editorRef.current.innerHTML = initialHtml || '';
      isInitialized.current = true;
    }
  }, [initialHtml]);

  const exec = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateActiveFormats();
    emitChange();
  }, []);

  const emitChange = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const updateActiveFormats = useCallback(() => {
    const formats = new Set<string>();
    if (document.queryCommandState('bold')) formats.add('bold');
    if (document.queryCommandState('italic')) formats.add('italic');
    if (document.queryCommandState('insertUnorderedList')) formats.add('ul');
    if (document.queryCommandState('insertOrderedList')) formats.add('ol');

    // Check block type
    const block = document.queryCommandValue('formatBlock');
    setActiveBlock(block || 'p');

    setActiveFormats(formats);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Ctrl/Cmd+B for bold
    if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
      e.preventDefault();
      exec('bold');
    }
    // Ctrl/Cmd+I for italic
    if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
      e.preventDefault();
      exec('italic');
    }
    // Ctrl/Cmd+K for link
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      openLinkInput();
    }
    // Tab for indent
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        exec('outdent');
      } else {
        exec('indent');
      }
    }
  }, [exec]);

  const handleInput = useCallback(() => {
    updateActiveFormats();
    emitChange();
  }, [updateActiveFormats, emitChange]);

  const handleSelect = useCallback(() => {
    updateActiveFormats();
  }, [updateActiveFormats]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    // Check for pasted images
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (file) {
            handlePastedImage(file);
          }
          return;
        }
      }
    }

    // For text, paste as clean HTML
    e.preventDefault();
    const html = e.clipboardData?.getData('text/html');
    const text = e.clipboardData?.getData('text/plain') || '';

    if (html) {
      // Clean up pasted HTML — keep only safe formatting
      const cleaned = cleanPastedHtml(html);
      document.execCommand('insertHTML', false, cleaned);
    } else {
      // Plain text — convert line breaks to paragraphs
      const formatted = text
        .split(/\n\n+/)
        .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
        .join('');
      document.execCommand('insertHTML', false, formatted || text);
    }
    emitChange();
  }, [emitChange]);

  const handlePastedImage = async (file: File) => {
    // Create a placeholder while uploading
    const placeholderId = 'img-upload-' + Date.now();
    document.execCommand('insertHTML', false,
      `<div id="${placeholderId}" class="my-4 p-4 bg-muted rounded-lg text-center text-sm text-muted-foreground">
        <span>Uploading image...</span>
      </div>`
    );
    emitChange();

    try {
      const url = await uploadImage(file);
      const placeholder = document.getElementById(placeholderId);
      if (placeholder) {
        placeholder.outerHTML = `<figure class="my-4"><img src="${url}" alt="" class="max-w-full rounded-lg" loading="lazy" /><figcaption contenteditable="true" class="text-center text-xs text-muted-foreground mt-1"></figcaption></figure>`;
      }
    } catch (err) {
      const placeholder = document.getElementById(placeholderId);
      if (placeholder) {
        placeholder.outerHTML = '<p class="text-red-500 text-sm">Image upload failed. Try again.</p>';
      }
    }
    emitChange();
  };

  const formatBlock = (tag: string) => {
    exec('formatBlock', `<${tag}>`);
  };

  const openLinkInput = () => {
    // Save current selection before opening input
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelection.current = sel.getRangeAt(0).cloneRange();
    }
    setShowLinkInput(true);
    setLinkUrl('');
    setTimeout(() => linkInputRef.current?.focus(), 50);
  };

  const insertLink = () => {
    if (!linkUrl.trim()) {
      setShowLinkInput(false);
      return;
    }
    // Restore selection
    if (savedSelection.current) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(savedSelection.current);
    }
    const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
    exec('createLink', url);
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const insertDivider = () => {
    document.execCommand('insertHTML', false, '<hr class="my-6 border-border" /><p><br></p>');
    editorRef.current?.focus();
    emitChange();
  };

  const toggleBlockquote = () => {
    // Check if we're already in a blockquote
    const sel = window.getSelection();
    let node = sel?.anchorNode as HTMLElement;
    let inBlockquote = false;
    while (node && node !== editorRef.current) {
      if (node.tagName === 'BLOCKQUOTE') {
        inBlockquote = true;
        break;
      }
      node = node.parentElement!;
    }
    if (inBlockquote) {
      exec('formatBlock', '<p>');
    } else {
      exec('formatBlock', '<blockquote>');
    }
  };

  const toggleCodeBlock = () => {
    const sel = window.getSelection();
    let node = sel?.anchorNode as HTMLElement;
    let inPre = false;
    while (node && node !== editorRef.current) {
      if (node.tagName === 'PRE') {
        inPre = true;
        break;
      }
      node = node.parentElement!;
    }
    if (inPre) {
      exec('formatBlock', '<p>');
    } else {
      document.execCommand('insertHTML', false,
        '<pre class="bg-muted rounded-lg p-4 overflow-x-auto text-sm font-mono my-4"><code><br></code></pre><p><br></p>'
      );
      editorRef.current?.focus();
      emitChange();
    }
  };

  const isActive = (format: string) => activeFormats.has(format);
  const isBlockActive = (tag: string) => activeBlock.toLowerCase() === tag;

  // Toolbar button component
  const ToolBtn = ({ active, onClick, title, children, className }: {
    active?: boolean; onClick: () => void; title: string; children: React.ReactNode; className?: string;
  }) => (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? 'bg-primary/15 text-primary'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      } ${className || ''}`}
    >
      {children}
    </button>
  );

  const Sep = () => <div className="w-px h-5 bg-border mx-0.5" />;

  return (
    <div className="rich-editor-wrap">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border-y border-border py-1.5 px-1 overflow-x-auto flex-wrap">
        {/* Block type dropdown-like buttons */}
        <ToolBtn active={isBlockActive('h2')} onClick={() => formatBlock('h2')} title="Heading 1">
          <Heading1 className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn active={isBlockActive('h3')} onClick={() => formatBlock('h3')} title="Heading 2">
          <Heading2 className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn active={isBlockActive('h4')} onClick={() => formatBlock('h4')} title="Heading 3">
          <Heading3 className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn active={isBlockActive('p') || isBlockActive('div') || isBlockActive('')} onClick={() => formatBlock('p')} title="Normal text">
          <Type className="w-4 h-4" />
        </ToolBtn>

        <Sep />

        {/* Inline formatting */}
        <ToolBtn active={isActive('bold')} onClick={() => exec('bold')} title="Bold (Ctrl+B)">
          <Bold className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn active={isActive('italic')} onClick={() => exec('italic')} title="Italic (Ctrl+I)">
          <Italic className="w-4 h-4" />
        </ToolBtn>

        <Sep />

        {/* Lists */}
        <ToolBtn active={isActive('ul')} onClick={() => exec('insertUnorderedList')} title="Bullet list">
          <List className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn active={isActive('ol')} onClick={() => exec('insertOrderedList')} title="Numbered list">
          <ListOrdered className="w-4 h-4" />
        </ToolBtn>

        <Sep />

        {/* Block elements */}
        <ToolBtn onClick={toggleBlockquote} title="Quote">
          <Quote className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={toggleCodeBlock} title="Code block">
          <Code className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={insertDivider} title="Divider">
          <Minus className="w-4 h-4" />
        </ToolBtn>

        <Sep />

        {/* Link & Image */}
        <ToolBtn onClick={openLinkInput} title="Insert link (Ctrl+K)">
          <LinkIcon className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={onImageUpload} title="Insert image">
          {uploadingInline ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
        </ToolBtn>

        <Sep />

        {/* Undo/Redo */}
        <ToolBtn onClick={() => exec('undo')} title="Undo (Ctrl+Z)">
          <Undo className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn onClick={() => exec('redo')} title="Redo (Ctrl+Shift+Z)">
          <Redo className="w-4 h-4" />
        </ToolBtn>
      </div>

      {/* Link input bar */}
      {showLinkInput && (
        <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border-b border-border">
          <LinkIcon className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            ref={linkInputRef}
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); insertLink(); }
              if (e.key === 'Escape') setShowLinkInput(false);
            }}
            placeholder="Paste or type a URL..."
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
          <button
            onClick={insertLink}
            className="px-2.5 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90"
          >
            Add
          </button>
          <button
            onClick={() => setShowLinkInput(false)}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onSelect={handleSelect}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        data-placeholder={placeholder || "Start writing your article..."}
        className="rich-editor min-h-[50vh] px-1 py-3 focus:outline-none text-[15px] leading-relaxed"
        style={{ wordBreak: 'break-word' }}
      />
    </div>
  );
}

// ── Clean pasted HTML ───────────────────────────────────────────────
function cleanPastedHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;

  // Remove scripts, styles, meta tags
  div.querySelectorAll('script, style, meta, link, title').forEach(el => el.remove());

  // Remove all style attributes, class attributes from pasted content
  div.querySelectorAll('*').forEach(el => {
    el.removeAttribute('style');
    el.removeAttribute('class');
    el.removeAttribute('id');
  });

  // Convert spans to their text content (remove formatting wrappers)
  div.querySelectorAll('span').forEach(span => {
    span.outerHTML = span.innerHTML;
  });

  return div.innerHTML;
}

// ── HTML to Markdown Converter ──────────────────────────────────────
// Converts the editor's HTML output to clean markdown for Nostr storage.

function htmlToMarkdown(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;

  function processNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const children = Array.from(el.childNodes).map(processNode).join('');

    switch (tag) {
      case 'h1': return `# ${children.trim()}\n\n`;
      case 'h2': return `## ${children.trim()}\n\n`;
      case 'h3': return `### ${children.trim()}\n\n`;
      case 'h4': return `#### ${children.trim()}\n\n`;
      case 'p': return children.trim() ? `${children.trim()}\n\n` : '\n';
      case 'br': return '\n';
      case 'strong':
      case 'b': return `**${children}**`;
      case 'em':
      case 'i': return `*${children}*`;
      case 'a': {
        const href = el.getAttribute('href') || '';
        return `[${children}](${href})`;
      }
      case 'img': {
        const src = el.getAttribute('src') || '';
        const alt = el.getAttribute('alt') || '';
        return `![${alt}](${src})\n\n`;
      }
      case 'figure': {
        // Handle figure with img and optional figcaption
        const img = el.querySelector('img');
        const caption = el.querySelector('figcaption');
        if (img) {
          const src = img.getAttribute('src') || '';
          const alt = caption?.textContent?.trim() || img.getAttribute('alt') || '';
          return `![${alt}](${src})\n\n`;
        }
        return children;
      }
      case 'ul': {
        const items = Array.from(el.querySelectorAll(':scope > li'));
        return items.map(li => `- ${processNode(li).replace(/^\s+|\s+$/g, '')}`).join('\n') + '\n\n';
      }
      case 'ol': {
        const items = Array.from(el.querySelectorAll(':scope > li'));
        return items.map((li, i) => `${i + 1}. ${processNode(li).replace(/^\s+|\s+$/g, '')}`).join('\n') + '\n\n';
      }
      case 'li': return children;
      case 'blockquote': {
        const lines = children.trim().split('\n').map(l => `> ${l}`);
        return lines.join('\n') + '\n\n';
      }
      case 'pre': {
        const code = el.querySelector('code');
        const text = code ? code.textContent : el.textContent;
        return '```\n' + (text || '').trim() + '\n```\n\n';
      }
      case 'code': {
        // Inline code (not inside pre)
        if (el.parentElement?.tagName.toLowerCase() !== 'pre') {
          return '`' + children + '`';
        }
        return children;
      }
      case 'hr': return '---\n\n';
      case 'div': return children.trim() ? `${children.trim()}\n\n` : '';
      default: return children;
    }
  }

  let md = Array.from(div.childNodes).map(processNode).join('');

  // Clean up excessive whitespace
  md = md.replace(/\n{3,}/g, '\n\n');
  md = md.trim();

  return md;
}

// ── Markdown to HTML (for loading existing articles into editor) ─────
function markdownToHtml(md: string): string {
  let html = md;

  // Code blocks first
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, _lang, code) =>
    `<pre class="bg-muted rounded-lg p-4 overflow-x-auto text-sm font-mono my-4"><code>${escapeHtml(code.trim())}</code></pre>`
  );

  // Inline code
  html = html.replace(/`([^`\n]+)`/g, '<code>$1</code>');

  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<figure class="my-4"><img src="$2" alt="$1" class="max-w-full rounded-lg" loading="lazy" /></figure>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Headings
  html = html.replace(/^#### (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // HR
  html = html.replace(/^---+$/gm, '<hr />');

  // Blockquotes
  html = html.replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>');

  // Bold + italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Unordered lists
  html = html.replace(/^[\-\*] (.+)$/gm, '<li>$1</li>');
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');

  // Ordered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Paragraphs — wrap remaining plain lines
  html = html.replace(/^(?!<[a-z/])((?!<).+)$/gm, '<p>$1</p>');

  // Clean empty paragraphs
  html = html.replace(/<p><\/p>/g, '');

  return html;
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ── BlogComposer Page ────────────────────────────────────────────────

export default function BlogComposer() {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const navigate = useNavigate();
  const location = useLocation();
  const base = inkBaseFor(location.pathname);
  const [searchParams] = useSearchParams();
  const editNaddr = searchParams.get('edit');

  const [title, setTitle] = useState('');
  const [editorHtml, setEditorHtml] = useState('');
  const [summary, setSummary] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [dTag, setDTag] = useState('');
  const [announce, setAnnounce] = useState(true); // share a Nostr announcement on publish
  const [publishing, setPublishing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingInline, setUploadingInline] = useState(false);
  const [coverUploadError, setCoverUploadError] = useState<string | null>(null);
  const [inlineUploadError, setInlineUploadError] = useState<string | null>(null);
  const [initialEditorHtml, setInitialEditorHtml] = useState('');
  const coverInputRef = useRef<HTMLInputElement>(null);
  const inlineInputRef = useRef<HTMLInputElement>(null);

  // ── Edit mode: load existing article ──────────────────────────
  const editDecoded = useMemo(() => {
    if (!editNaddr) return null;
    try {
      const { type, data } = nip19.decode(editNaddr);
      if (type === 'naddr') return data as { identifier: string; pubkey: string; kind: number };
    } catch {}
    return null;
  }, [editNaddr]);

  const { events: editEvents } = useNostrQuery({
    filters: editDecoded ? [{
      kinds: [30023],
      authors: [editDecoded.pubkey],
      '#d': [editDecoded.identifier],
      limit: 1,
    }] : [],
    enabled: !!editDecoded,
  });

  // Populate form from existing article
  useEffect(() => {
    if (editEvents.length === 0) return;
    const ev = editEvents[0];
    setTitle(ev.tags.find((t: string[]) => t[0] === 'title')?.[1] || '');
    setSummary(ev.tags.find((t: string[]) => t[0] === 'summary')?.[1] || '');
    setCoverImage(ev.tags.find((t: string[]) => t[0] === 'image')?.[1] || '');
    setDTag(ev.tags.find((t: string[]) => t[0] === 'd')?.[1] || '');
    const tags = ev.tags.filter((t: string[]) => t[0] === 't').map((t: string[]) => t[1]);
    setTagsInput(tags.join(', '));
    // Convert existing markdown to HTML for the editor
    const html = markdownToHtml(ev.content);
    setInitialEditorHtml(html);
    setEditorHtml(html);
  }, [editEvents]);

  // Generate d-tag from title (slug)
  useEffect(() => {
    if (editDecoded) return;
    if (title) {
      const slug = title.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .slice(0, 80);
      setDTag(slug);
    }
  }, [title, editDecoded]);

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    setCoverUploadError(null);
    try {
      const url = await uploadImage(file);
      setCoverImage(url);
    } catch (err) {
      console.error('Cover upload failed:', err);
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setCoverUploadError(msg.includes('All upload services failed')
        ? 'Image upload failed. Try pasting an image URL below instead, or try a smaller image.'
        : msg
      );
    }
    setUploadingCover(false);
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  const handleInlineImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingInline(true);
    setInlineUploadError(null);
    try {
      const url = await uploadImage(file);
      // Insert image into the contenteditable editor
      const editor = document.querySelector('.rich-editor') as HTMLDivElement;
      if (editor) {
        editor.focus();
        document.execCommand('insertHTML', false,
          `<figure class="my-4"><img src="${url}" alt="" class="max-w-full rounded-lg" loading="lazy" /></figure><p><br></p>`
        );
        setEditorHtml(editor.innerHTML);
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      setInlineUploadError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    }
    setUploadingInline(false);
    if (inlineInputRef.current) inlineInputRef.current.value = '';
  };

  const handlePublish = async () => {
    if (!user || !title.trim() || !editorHtml.trim()) return;
    setPublishing(true);

    try {
      // Convert editor HTML to markdown
      const markdownContent = htmlToMarkdown(editorHtml);

      // In edit mode, always use the original d-tag from the URL (editDecoded.identifier)
      // so the published event replaces the existing one instead of creating a new post.
      // Fall back to the dTag state (for new posts) or generate a slug from the title.
      const effectiveDTag = editDecoded
        ? editDecoded.identifier
        : (dTag || title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 80) || 'untitled-' + Date.now());

      const tags: string[][] = [
        ['d', effectiveDTag],
        ['title', title.trim()],
      ];

      if (summary.trim()) tags.push(['summary', summary.trim()]);
      if (coverImage.trim()) tags.push(['image', coverImage.trim()]);

      const now = Math.floor(Date.now() / 1000);
      if (!editDecoded) {
        tags.push(['published_at', String(now)]);
      } else {
        const originalPub = editEvents[0]?.tags.find((t: string[]) => t[0] === 'published_at')?.[1];
        if (originalPub) tags.push(['published_at', originalPub]);
        else tags.push(['published_at', String(now)]);
      }

      const hashTags = tagsInput.split(/[,\s]+/).map(t => t.replace(/^#/, '').trim()).filter(Boolean);
      if (!hashTags.includes('bch')) hashTags.unshift('bch');
      hashTags.forEach(t => tags.push(['t', t.toLowerCase()]));

      const signedEvent = await publish({
        kind: 30023,
        content: markdownContent.trim(),
        tags,
        created_at: now,
      });

      const naddr = nip19.naddrEncode({
        identifier: effectiveDTag,
        pubkey: user.pubkey,
        kind: 30023,
      });

      // ── Optional Nostr announcement ────────────────────────────
      // Share a short kind 1 note that references the article by its
      // addressable coordinate (an `a` tag), so it shows up in the BCHNostr
      // feed and links back to the same article — no duplicate content.
      // Only for brand-new articles (not edits), and only if the author opted
      // in. Uses the same publisher / identity as the article itself, so a
      // Business Page announces as itself.
      if (announce && !editDecoded) {
        const url = inkArticleUrl(naddr);
        const authorName = user.name || 'a BCH writer';
        const plain = markdownContent.replace(/[#*_[\]`>!()-]/g, ' ').replace(/\s+/g, ' ').trim();
        const blurb = (summary.trim() || plain.slice(0, 140)).replace(/\s+/g, ' ');
        publish({
          kind: 1,
          content: `${INK_MARK} New on ${INK_NAME}\n\n"${title.trim()}"\nby ${authorName}\n\n${blurb ? blurb + '\n\n' : ''}Read it \u2192 ${url}\n\n\uD83D\uDC9A Tip \u00B7 \uD83D\uDCAC Discuss\n\n#bch #bitcoincash #bchink`,
          tags: [
            ['t', 'bch'], ['t', 'bitcoincash'], ['t', 'bchink'],
            ['a', `30023:${user.pubkey}:${effectiveDTag}`, '', 'mention'],
            ['r', url],
          ],
          created_at: now,
        }).catch(err => console.warn('Announcement failed (article still published):', err));
      }

      // Pass published event via router state so ArticlePage can display it
      // immediately without waiting for relays
      navigate(inkArticlePath(naddr, base), { state: { publishedEvent: signedEvent } });
    } catch (err) {
      console.error('Publish failed:', err);
    }
    setPublishing(false);
  };

  if (!user) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Log in to write articles.</p>
        <Link to={base} className="text-primary text-sm hover:underline mt-2 inline-block">Back to {INK_NAME}</Link>
      </div>
    );
  }

  // Calculate word count from editor HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = editorHtml;
  const textContent = tempDiv.textContent || '';
  const wordCount = textContent.split(/\s+/).filter(Boolean).length;
  const readingTime = Math.max(1, Math.round(wordCount / 200));

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 z-10 bg-background/80 backdrop-blur-md">
        <Link to={base} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to {INK_NAME}
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPreview(!preview)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              preview
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {preview ? <PenSquare className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {preview ? 'Edit' : 'Preview'}
          </button>
          <button
            onClick={handlePublish}
            disabled={publishing || !title.trim() || !editorHtml.trim()}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {editDecoded ? 'Update' : 'Publish'}
          </button>
        </div>
      </div>

      {preview ? (
        /* ── Preview Mode ── */
        <div className="px-4 sm:px-6 py-6">
          {coverImage && (
            <div className="aspect-[2.5/1] overflow-hidden bg-muted rounded-lg mb-6">
              <img src={coverImage} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">{title || 'Untitled'}</h1>
          {summary && <p className="text-muted-foreground mb-4">{summary}</p>}
          <div className="text-xs text-muted-foreground mb-6">{readingTime} min read · {wordCount} words</div>
          <div className="prose-bch">
            <PreviewMarkdown content={htmlToMarkdown(editorHtml)} />
          </div>
        </div>
      ) : (
        /* ── Edit Mode ── */
        <div className="p-4 space-y-4">
          {/* Cover image */}
          <div>
            {coverImage ? (
              <div className="relative aspect-[2.5/1] overflow-hidden bg-muted rounded-lg">
                <img src={coverImage} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => setCoverImage('')}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70"
                >
                  <X className="w-4 h-4" />
                </button>
                {/* Change cover button */}
                <button
                  onClick={() => coverInputRef.current?.click()}
                  className="absolute bottom-2 right-2 px-3 py-1.5 rounded-lg bg-black/50 text-white text-xs font-medium hover:bg-black/70 transition-colors"
                >
                  <Camera className="w-3.5 h-3.5 inline mr-1" />
                  Change
                </button>
              </div>
            ) : (
              <div
                onClick={() => !uploadingCover && coverInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('border-primary', 'bg-primary/10'); }}
                onDragLeave={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/10'); }}
                onDrop={async e => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
                  const file = e.dataTransfer.files?.[0];
                  if (file && file.type.startsWith('image/')) {
                    setUploadingCover(true);
                    setCoverUploadError(null);
                    try {
                      const url = await uploadImage(file);
                      setCoverImage(url);
                    } catch (err) {
                      setCoverUploadError(err instanceof Error ? err.message : 'Upload failed');
                    }
                    setUploadingCover(false);
                  }
                }}
                className={`w-full aspect-[3/1] border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 transition-colors cursor-pointer ${
                  uploadingCover ? 'border-primary/40 bg-primary/5' : 'border-border hover:border-primary/40 hover:bg-primary/5'
                }`}
              >
                {uploadingCover ? (
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-muted-foreground/40" />
                )}
                <span className="text-xs text-muted-foreground">
                  {uploadingCover ? 'Uploading cover image...' : 'Click or drag an image to add a cover'}
                </span>
              </div>
            )}
            {coverUploadError && (
              <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg bg-red-500/10 text-red-500 text-xs">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1">{coverUploadError}</span>
                <button onClick={() => setCoverUploadError(null)} className="shrink-0">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
            {/* Paste URL fallback */}
            {!coverImage && !uploadingCover && (
              <div className="mt-2">
                <input
                  type="text"
                  placeholder="Or paste an image URL..."
                  className="w-full bg-muted rounded-lg px-3 py-2 text-xs text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground/30"
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const val = (e.target as HTMLInputElement).value.trim();
                      if (val && (val.startsWith('http://') || val.startsWith('https://'))) {
                        setCoverImage(val);
                        (e.target as HTMLInputElement).value = '';
                      }
                    }
                  }}
                  onPaste={e => {
                    // Auto-set if pasting a URL
                    setTimeout(() => {
                      const val = (e.target as HTMLInputElement).value.trim();
                      if (val && (val.startsWith('http://') || val.startsWith('https://')) && (val.match(/\.(jpg|jpeg|png|gif|webp|svg)/i) || val.includes('nostr.build') || val.includes('blossom'))) {
                        setCoverImage(val);
                        (e.target as HTMLInputElement).value = '';
                      }
                    }, 50);
                  }}
                />
              </div>
            )}
          </div>

          {/* Title */}
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Article title..."
            className="w-full text-2xl sm:text-3xl font-bold bg-transparent focus:outline-none placeholder:text-muted-foreground/30"
          />

          {/* Summary */}
          <input
            value={summary}
            onChange={e => setSummary(e.target.value)}
            placeholder="Brief summary (appears in preview cards)..."
            className="w-full text-sm bg-transparent focus:outline-none placeholder:text-muted-foreground/30 text-muted-foreground"
          />

          {/* Rich Text Editor */}
          <RichEditor
            initialHtml={initialEditorHtml}
            onChange={setEditorHtml}
            onImageUpload={() => inlineInputRef.current?.click()}
            uploadingInline={uploadingInline}
            placeholder="Start writing your article..."
          />
          <input ref={inlineInputRef} type="file" accept="image/*" className="hidden" onChange={handleInlineImageUpload} />

          {/* Inline upload error */}
          {inlineUploadError && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 text-red-500 text-xs">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1">{inlineUploadError}</span>
              <button onClick={() => setInlineUploadError(null)} className="shrink-0">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Word count */}
          <div className="text-right">
            <span className="text-[10px] text-muted-foreground">{wordCount} words · {readingTime} min read</span>
          </div>

          {/* Tags */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Tags (comma separated)</label>
            <input
              value={tagsInput}
              onChange={e => setTagsInput(e.target.value)}
              placeholder="bch, bitcoincash, tutorial"
              className="w-full bg-muted rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">The "bch" tag is added automatically so your article appears in the community feed.</p>
          </div>

          {/* Announce on Nostr — only for new articles */}
          {!editDecoded && (
            <label className="flex items-start gap-2.5 rounded-xl border border-border bg-card px-3.5 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={announce}
                onChange={e => setAnnounce(e.target.checked)}
                className="mt-0.5 accent-[var(--color-primary)]"
              />
              <span className="text-xs text-muted-foreground leading-relaxed">
                <span className="font-medium text-foreground">Announce on Nostr</span> — share a short note
                to the BCHNostr feed linking back to this article so people can read, tip, and discuss it.
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}

// ── Preview Markdown Renderer ───────────────────────────────────────
function PreviewMarkdown({ content }: { content: string }) {
  const html = useMemo(() => {
    let h = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    h = h.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, _lang, code) =>
      `<pre class="bg-muted rounded-lg p-4 overflow-x-auto text-sm font-mono my-4"><code>${code.trim()}</code></pre>`
    );
    h = h.replace(/`([^`\n]+)`/g, '<code class="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');
    h = h.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="max-w-full rounded-lg my-4" loading="lazy" />');
    h = h.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>');
    h = h.replace(/^#### (.+)$/gm, '<h4 class="text-base font-bold mt-6 mb-2">$1</h4>');
    h = h.replace(/^### (.+)$/gm, '<h3 class="text-lg font-bold mt-6 mb-2">$1</h3>');
    h = h.replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold mt-8 mb-3">$1</h2>');
    h = h.replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-8 mb-3">$1</h1>');
    h = h.replace(/^---+$/gm, '<hr class="my-6 border-border" />');
    h = h.replace(/^&gt; (.+)$/gm, '<blockquote class="border-l-4 border-primary/30 pl-4 py-1 my-3 text-muted-foreground italic">$1</blockquote>');
    h = h.replace(/\*\*\*(.+?)\*\*\*/g, '<strong class="font-bold"><em>$1</em></strong>');
    h = h.replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold">$1</strong>');
    h = h.replace(/\*(.+?)\*/g, '<em>$1</em>');
    h = h.replace(/^[\-\*] (.+)$/gm, '<li class="ml-4 list-disc">$1</li>');
    h = h.replace(/((?:<li class="ml-4 list-disc">.*<\/li>\n?)+)/g, '<ul class="my-3 space-y-1">$1</ul>');
    h = h.replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>');
    h = h.replace(/((?:<li class="ml-4 list-decimal">.*<\/li>\n?)+)/g, '<ol class="my-3 space-y-1">$1</ol>');
    h = h.replace(/^(?!<[a-z])((?!<).+)$/gm, '<p class="my-3 leading-relaxed">$1</p>');
    h = h.replace(/<p class="my-3 leading-relaxed"><\/p>/g, '');

    return h;
  }, [content]);

  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

